/* ─── Hydrate the prerendered HTML, or render fresh? ───
 * vite-react-ssg always hydrates in production, but two kinds of page load
 * can't match the HTML in #root, and React then fails with error #418 (and
 * throws the HTML away anyway, after reporting the error to Sentry):
 *
 *  1. Routes that aren't prerendered (/dashboard, /guest/portal/…): the
 *     Cloudflare SPA fallback serves the prerendered `/` page for them.
 *     vite.config.ts stamps each prerendered page with `data-ssg-path`, so we
 *     can tell the copy of `/` apart from the real thing.
 *  2. Pages without a language in the URL (/, /pricing, /about…) are built in
 *     English, but a Croatian (or German…) browser renders them in its own
 *     language. Blog pages take their language from the URL, so they match.
 *
 * In those cases main.tsx asks the (patched) library for a fresh client
 * render — the same thing React did after the error, minus the error.
 */

/** The i18n language the SSG pass renders pages in. */
export const SSG_LANGUAGE = 'en';

/** Prerendered routes whose text comes from the URL language, not the browser. */
const URL_LANGUAGE_PAGE = /^(hr|en|sl|de|it)\/blog(\/|$)/;

const trimSlashes = (p: string) => p.replace(/^\/+|\/+$/g, '');

export function canHydratePrerender(currentLanguage: string): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.getElementById('root');
  if (root?.getAttribute('data-server-rendered') !== 'true') return false;

  let here = window.location.pathname;
  try { here = decodeURIComponent(here); } catch { /* keep the raw path */ }
  here = trimSlashes(here);

  const ssgPath = root.getAttribute('data-ssg-path');
  // No stamp = HTML from an older build; hydrate as before.
  if (ssgPath !== null && trimSlashes(ssgPath) !== here) return false;

  if (URL_LANGUAGE_PAGE.test(here)) return true;
  return currentLanguage === SSG_LANGUAGE;
}
